// /src/pages/CheckoutPage.tsx
import React, { useState, useEffect } from "react";
import { fetchPaymentMethods, completePayment, fetchOrderDetails, updateTableStatus } from "../utils/api";
import { OrderSummary, PaymentPayload, PaymentMethod } from "../utils/types";
import { useSettings } from "../context/SettingsContext";
import { useParams, useNavigate } from "react-router-dom";
import { FaArrowLeftLong } from "react-icons/fa6";
import KeyPad from "../components/KeyPad";
import { displayPrice } from '../utils/general';
// import { FaRightLong } from "react-icons/fa6";
// import { table } from "console";
// import OrderTypeModal from "../components/OrderTypeModal";
// import OrderDetails from "./OrderDetails";
import { useCart } from '../context/CartContext'; // Import the cart context
import { sendToPrinter } from "../utils/api";
import { Tables } from '../utils/types';


const CheckoutPage: React.FC = () => {
    const { settings } = useSettings();
    const { orderId, tableId } = useParams<{ orderId: string, tableId: string }>();

    const navigate = useNavigate();
    const { cartItems, updateQuantity, removeFromCart, clearCart, setCartItems } = useCart();

    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
    const [orderDetails, setOrderDetails] = useState<OrderSummary | null>(null);

    const [receivedAmount, setReceivedAmount] = useState<number>(0); // Final numeric value
    const [inputValue, setInputValue] = useState<string>(""); // Input string for the keypad

    const [tables, setTables] = useState<Tables[]>([]);

    useEffect(() => {
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        const vendorId = userData.vendor_id;
        const numericOrderId = parseInt(orderId || '', 10);

        if (isNaN(numericOrderId)) {
            console.error("Invalid order ID");
            return;
        }

        fetchPaymentMethods(vendorId).then(setPaymentMethods);
        // fetchOrderDetails(vendorId, numericOrderId).then(setOrderDetails);
        const fetchOrder = async () => {
            try {
                if (settings && orderId) {
                    const orderDetails = await fetchOrderDetails(settings.vendorId, numericOrderId);
                    // console.log('orderDetails page: ', orderDetails);
                    setOrderDetails(orderDetails);
                }
            } catch (error) {
                console.error("Error fetching order details:", error);
            } finally {
                // setLoading(false);
            }
        };
        fetchOrder();
        // console.log('orderDetails: ', orderDetails, vendorId, numericOrderId);
        // setReceivedAmount(60); // Example default value
    }, [orderId, settings?.vendorId]);


    // Effect to sync inputValue with receivedAmount
    useEffect(() => {
        setReceivedAmount(parseFloat(inputValue) || 0); // Convert to number
    }, [inputValue]);

    // Define the tax object properties type
    interface TaxSetting {
        vendorId: number;
        taxId: number;
        type: 'percentage' | 'fixed'; // Assuming only 'percentage' and 'fixed' are possible values
        taxAmount: string; // If stored as a string, otherwise use `number`
        name: string;
    }


    // Define the taxCalculate function
    const taxCalculate = (
        amountToPay: number,
        tax: [number, string, number, string]
    ): [number, number] => {
        let taxAmount = 0;
        let totalAmount = 0;

        if (tax[1] === "percentage") {
            taxAmount = (amountToPay * tax[2]) / 100;
        } else {
            taxAmount = tax[2];
        }

        totalAmount = amountToPay + taxAmount;


        // Round totalAmount to two decimal places
        totalAmount = parseFloat(totalAmount.toFixed(2));

        return [taxAmount, totalAmount];
    };

    // Access tax settings from localStorage
    const taxSettings: TaxSetting[] = JSON.parse(localStorage.getItem("taxsettings") || "[]");

    // Assuming taxSettings is an array of objects with the structure you're working with:
    // const taxSettings = JSON.parse(localStorage.getItem('taxsettings') || '[]');

    const taxSST = taxSettings.find((tax) => tax.name === "SST");
    const taxGST = taxSettings.find((tax) => tax.name === "GST");

    // console.log('taxSST > ', taxSettings[0]);
    // console.log('taxSST > ', taxSST);
    // console.log('taxGST > ', taxGST);

    // Safely extract tax properties if taxSettings[0] exists
    // const taxSST = (taxSettings[0] && taxSettings[0][0]) as TaxSetting | undefined;
    // const taxGST = (taxSettings[0] && taxSettings[0][1]) as TaxSetting | undefined;

    const tax_SST: [number, string, number, string] = [
        taxSST?.taxId || 0,
        taxSST?.type || "percentage",
        parseFloat(taxSST?.taxAmount || "0"),
        taxSST?.name || "",
    ];

    const tax_GST: [number, string, number, string] = [
        taxGST?.taxId || 0,
        taxGST?.type || "fixed",
        parseFloat(taxGST?.taxAmount || "0"),
        taxGST?.name || "",
    ];

    // Calculate total with SST
    // const subtotal = 22.80;
    // const [sstAmount, totalWithSST] = taxCalculate(subtotal, tax_SST);

    // console.log(`Subtotal: ${subtotal}`);
    // console.log(`SST Amount (9%): ${sstAmount.toFixed(2)}`);
    // console.log(`Total with SST: ${totalWithSST.toFixed(2)}`);


    const amountToPay: number = parseFloat(orderDetails?.amount_to_pay || "0");
    const [taxAmount_SST, totalAmount_SST] = taxCalculate(amountToPay, tax_SST);
    const [taxAmount_GST, totalAmount_GST] = taxCalculate(amountToPay, tax_GST);

    // console.log(orderDetails);
    // console.log('taxSST > ', taxAmount_SST, totalAmount_SST);
    // console.log('taxGST > ', taxAmount_GST, totalAmount_GST);

    // const amountToPay = parseFloat(orderDetails.amount_to_pay);
    const receivable = totalAmount_SST; //orderDetails.totalWithTax,
    const roundedReceivable = receivable
        ? Math.round(receivable * 10) / 10
        : 0; // Default value if receivable is undefined or null    // const receivedAmount = receivedAmount,

    const roundingValue = totalAmount_SST - roundedReceivable;

    const change = receivedAmount <= 0 || receivedAmount < totalAmount_SST
        ? 0
        : receivedAmount + roundingValue - totalAmount_SST;

    const serviceCharge = [];
    const totalTaxes = taxAmount_SST;
    // setInputValue(roundedReceivable.toString());

    const handlePayment = async () => {

        if (!selectedPaymentMethod || !orderDetails) return;

        const vendorId = settings?.vendorId || 0;

        const paymentPayload: PaymentPayload = {
            orderId: orderDetails.id,
            paymentType: selectedPaymentMethod,
            status: 'paid',
            amountToPay: parseFloat(orderDetails.amount_to_pay),
            receivable: totalAmount_SST, //orderDetails.totalWithTax,
            roundedReceivable,
            receivedAmount,
            change,
            serviceCharge: [],
            totalTaxes: taxAmount_SST,
            roundingValue,
        };

        try {
            const response = await completePayment(vendorId, paymentPayload);
            // console.log(response);

            if (orderDetails.table_id) {
                try {
                    await updateTableStatus(vendorId, orderDetails.table_id, 'vacant', null, null);
                } catch (error) {
                    console.error('Error updating table status:', error);
                }
            }

            // alert('Payment processed successfully');

            clearCart();
            localStorage.removeItem('existingOrderId');
            localStorage.removeItem('checkedOutItems');
            localStorage.removeItem('selectedTable');
            localStorage.removeItem('orderId');
            setCartItems([]);   // Clear the cart

            localStorage.removeItem('');
            setTables([]);

            handlePrintRecipt();

            // navigate(`/order-details/${orderDetails.id}`);
            navigate('/tables');

        } catch (error) {
            alert('Payment failed, please try again');
        }
    };


    const handlePrintRecipt = async () => {

        const vendorId = settings?.vendorId || 0;
        const order_id = orderDetails?.id || 0;


        try {
            const response = await sendToPrinter(vendorId, order_id, null, null); // Pass null for printEvent and printerLocation if not needed (defaults to "order_placed", "kitchen");
            if (response.success) {
                console.log("Print success:", response);
                // alert("Receipt printed successfully!");
            } else {
                console.log("Print failed:", response);
                alert("Failed to print receipt.");
            }
        } catch (error) {
            console.error("Error during print test:", error);
            alert("An error occurred while printing the receipt.");
        }
    };

    const handleBackNavigation = () => {
        const referrer = document.referrer;
        const isSameDomain = referrer && new URL(referrer).origin === window.location.origin;

        if (isSameDomain) {
            // Navigate back if the referrer is within the same domain
            navigate(-1);
        } else {
            // Otherwise, navigate to the home page
            navigate(-1);
        }
    };

    
    const [isKeypadVisible, setIsKeypadVisible] = useState(false);
    const [discount_price, setDiscount_price] = useState(false);

    // console.log(selectedPaymentMethod);

    return (
        <div className='app-page'>
            <div className='w-full h-full overflow-x-hidden overflow-y-auto flex flex-col justify-start items-start p-1 mt-22 mb-8 '>
                <div className="w-full flex flex-row justify-between">
                    <button onClick={handleBackNavigation} className="text-lg text-blue-600 p-1" aria-label="Close">
                        <FaArrowLeftLong />
                    </button>
                </div>
                <h2 className="pageTitle ">Payment</h2>

                <div className='w-full h-full flex flex-col md:flex-row justify-start place-items-start overflow-x-hidden overflow-y-aut shadow-inner	'>

                    <div className='w-full md:w-2/3  flex flex-row md:flex-row justify-center place-items-center '>

                        <div className="w-1/2 min-h-72 text-left font-normal mt-2 mb-2 px-4 pt-4 pb-0  bg-white border-r border-r-1 border-r-gray-400 ">

                            <p className="my-1 text-sm font-bold">Order #{orderDetails?.id}</p>
                            {orderDetails?.table_id && (
                                <p className="my-1 text-sm font-bold">{orderDetails?.order_type} -  {orderDetails?.table?.table_name} at {orderDetails?.table?.location}</p>
                            )}
                            {orderDetails?.order_type !== 'Dine In' && (
                                <p className="my-1 text-sm font-bold">{orderDetails?.order_type}</p>
                            )}
                            <p className="my-1 text-sm font-bold text-red-400">Order Status: {orderDetails?.status.toUpperCase()}</p>

                            <p className="my-1">Subtotal: {displayPrice(orderDetails?.amount_to_pay, settings?.currencyName)}</p>
                            <p className="my-1">SST ({taxSST?.taxAmount}%): {displayPrice(taxAmount_SST, settings?.currencyName)}</p>
                            {/* <p className="my-1">Subtotal with SST: {displayPrice(totalAmount_SST, settings?.currencyName)}</p> */}
                            <p className="my-1">Bill Rounding: {displayPrice(roundingValue, settings?.currencyName)}</p>
                            <p className="my-1">Receivable: <span className="text-lg font-bold">{displayPrice(roundedReceivable, settings?.currencyName)}</span></p>
                            <p className="my-1">Paid: <span className="text-lg font-bold text-green-600">{displayPrice(receivedAmount, settings?.currencyName)}</span></p>
                            <p className="my-1">Change: <span className="text-lg font-bold text-red-600">{displayPrice(change, settings?.currencyName)}</span></p>

                        </div>

                        <div className="w-1/2 min-h-72 text-left font-semibold mt-2 mb-2 px-2 md:px-8 pt-4 pb-0 rounded-3xl bg-white">
                            <p className="my-1 text-sm font-bold">Select Payment Method:</p>
                            <p className="my-1 text-sm font-bold text-red-400">&nbsp;</p>

                            <div className="flex flex-col items-start mb-4 text-left text-sm">
                                {paymentMethods.map((method) => (
                                    <label key={method.id} className="flex items-start px-1 py-0 mx-2 my-1">
                                        <input
                                            type="radio"
                                            name="paymentMethod"
                                            value={method.name}
                                            checked={selectedPaymentMethod === method.name}
                                            // onChange={() => setSelectedPaymentMethod(method.name)}
                                            onChange={() => {

                                                let fullPayment: number = 0;

                                                setSelectedPaymentMethod(method.name);

                                                // Automatically set inputValue and receivedAmount based on the payment method
                                                if (["Cash test"].includes(method.name)) {
                                                    fullPayment = 0;

                                                    setInputValue('');
                                                    setReceivedAmount(0);

                                                } else {
                                                    fullPayment = roundedReceivable;

                                                    // Ensure setInputValue gets a string
                                                    setInputValue(fullPayment.toString());

                                                    // setReceivedAmount expects a number, so no need to convert
                                                    setReceivedAmount(fullPayment);
                                                }
                                            }}
                                            className="form-radio mr-2 w-4 h-4"
                                        />
                                        <span>{method.name.toUpperCase()}</span>
                                    </label>
                                ))}
                                {/* {totalAmount_SST} */}
                            </div>

                            <div className="w-full flex flex-col items-start px-1 py-0 mx-1 my-1 mt-4">
                                Discount:
                                <input
                                    type="text"
                                    // value={discount_price}
                                    // onChange={(e) => setDiscount_price(e.target.value)}
                                    className="mt-2 m-0 px-1 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        </div>

                    </div>

                    {/* <div className=" md:hidden fixed bottom-28 right-4 z-50 sm:flex sm:flex-row">
                        <button
                            className="text-lg sm:block hidden p-2 bg-blue-500 text-white rounded-lg shadow-lg "
                            onClick={() => setIsKeypadVisible(!isKeypadVisible)}
                        >
                            Toggle Keypad
                        </button>
                        <div className="flex flex-row"> <FaRightLong size={20} className="m-2" /></div>
                    </div> */}

                    <div className={`w-full  md:w-1/3 md:fixed md:top-16  md:right-0 flex flex-col justify-start place-items-center px-2 py-1 mb-8 rounded-3xl   bg-white sm:rounded-3xl sm:transition-transform`} style={{ backgroundColor: '#7a75b5' }} >

                        {/* <div className={`w-full  md:w-1/3 md:relative flex flex-col justify-start place-items-center px-2 py-4 rounded-3xl  fixed sm:top-16 sm:right-5 sm:w-[90%] sm:h-[90%] bg-white sm:z-40 sm:rounded-t-lg sm:transition-transform ${isKeypadVisible
                            ? "sm:animate-slideIn"
                            : "sm:animate-slideOut sm:translate-x-[90%]"
                            }`}> */}

                        <div className="bg-transparent  p-2 rounded-xl">

                            {/* LED Screen */}
                            {/* <div className="w-full mb-4 h-16 bg-gray-50 text-green-700 text-right text-3xl p-4 rounded-lg font-mono shadow-md overflow-hidden">
                                {inputValue || "0"}
                            </div> */}
                            <KeyPad
                                onInputChange={(value) => {
                                    if (typeof value === "function") {
                                        setInputValue((prev) => value(prev));
                                    } else {
                                        setInputValue(value);
                                    }
                                }}
                                handlePayment={handlePayment}
                                selectedPaymentMethod={selectedPaymentMethod}
                                receivedAmount={receivedAmount}
                                totalAmount_SST={totalAmount_SST - roundingValue}
                            />
                        </div>
                    </div>
                </div>

            </div>
        </div >
    );
};

export default CheckoutPage;
